/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { useMemo, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';

import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import installmentPlanService from 'service/installmentPlan';
import PageBreadcrumb, {
  PageBreadcrumbItem
} from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import InstallmentPlanAdd from './InstallmentPlanAdd';

export const installmentBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'installment',
    url: '/installment'
  },
  {
    label: 'installment_plan',
    active: true
  }
];

export type InstallmentPlanType = {
  name: string;
  variation: string;
  barcode: string;
  original_barcode: string;
  unit: string;
  gupid: number;
  status: string;
};

const InstallmentPlan = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const installmentPlanPid = pathname.split('/').pop();

  const [openInstallment, setOpenInstallment] = useState(false);
  const [selectedInstallmentId, setSelectedInstallmentId] = useState<any>(0);

  //Get installment
  const { data, refetch } = useQuery(['GET_INSTALLMET_PLAN'], () => {
    if (installmentPlanPid) {
      const res = installmentPlanService
        .getInstallmentById(installmentPlanPid, { offset: 0, limit: 100 })
        .then((res: any) => res?.items);
      return res;
    }
  });

  const installmentData: InstallmentPlanType[] = useMemo(() => {
    return (
      data?.map((items: any) => {
        return {
          additional_percentage: items?.additional_percentage,
          initial_payment: items?.initial_payment,
          month: items?.month,
          gupid: items?.pid,
          status: items?.status === true ? t('active') : t('blocked')
        };
      }) ?? []
    );
  }, [data, t, i18n?.language]);

  const installmentTableColumns: ColumnDef<InstallmentPlanType>[] = [
    {
      id: 'action',
      cell: rowData => (
        <div className="d-flex gap-2">
          <Button
            className="p-2"
            variant="hover"
            onClick={() => {
              setOpenInstallment(true);
              setSelectedInstallmentId(rowData?.row?.original?.gupid);
            }}
          >
            <FeatherIcon icon="edit-2" className="cursor-pointer" size={20} />
          </Button>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'additional_percentage',
      header: t('additional_percentage'),
      meta: {
        cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
      }
    },

    {
      accessorKey: 'initial_payment',
      header: t('initial_payment'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'month',
      header: t('month'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status } = rowData?.row?.original || {};
        /* prettier-ignore */
        const defaultColors: any = {
          [t('active')]: { bg: '#BBFBD0', text: '#119C2B' },
          [t('blocked')]: { bg: '#FDDFDD', text: '#F2271C' }
        };
        /* prettier-ignore */
        const backgroundColor =
         defaultColors[status]?.bg || '#ffffff'; // Default to white if not specified
        const textColor = defaultColors[status]?.text || '#000000';

        return (
          <div className="d-flex">
            <p
              style={{
                background: backgroundColor,
                padding: '8px 10px',
                textAlign: 'center',
                borderRadius: '8px',
                color: textColor,
                fontWeight: '600',
                margin: '0'
              }}
            >
              {status}
            </p>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: installmentData,
    columns: installmentTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <>
      <PageBreadcrumb items={installmentBreadcrumbItems} />

      <div className="mb-9">
        <h2 className="mb-5">{t('installment_plan')}</h2>
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex justify-content-between gap-2">
              <SearchBox
                placeholder={t('search')}
                onChange={handleSearchInputChange}
              />
              <div className="d-flex justify-content-between gap-2">
                <Button
                  className="d-flex align-items-center gap-2"
                  variant="primary"
                  onClick={() => setOpenInstallment(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {t('add')}
                </Button>
              </div>
            </div>
          </div>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
            <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
            <AdvanceTableFooter pagination />
          </div>

          <Offcanvas
            show={openInstallment}
            onHide={() => {
              setOpenInstallment(false);
              setSelectedInstallmentId(0);
            }}
            placement="end"
            backdrop="static"
            keyboard={false}
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
                {t('installment_plan')}
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <InstallmentPlanAdd
                installmentData={installmentData}
                setSelectedInstallmentId={setSelectedInstallmentId}
                selectedInstallmentId={selectedInstallmentId}
                setOpenInstallment={setOpenInstallment}
                refetch={refetch}
              />
            </Offcanvas.Body>
          </Offcanvas>
        </AdvanceTableProvider>
      </div>
    </>
  );
};

export default InstallmentPlan;
